/**
 * Get day name from date (monday, tuesday, etc.)
 */
export const getDayName = (date) => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[date.getDay()];
};

/**
 * Get today's opening hours for display. Returns { openingTime, closingTime } or null if closed/no hours.
 * Use this when the backend has per-day openingHours (monday, tuesday, ...).
 */
export const getTodayOpeningHours = (restaurantOrStore) => {
  if (!restaurantOrStore?.openingHours) return null;
  const dayName = getDayName(new Date());
  const dayHours = restaurantOrStore.openingHours[dayName];
  if (!dayHours || dayHours.isOpen === false) return null;
  if (!dayHours.openingTime || !dayHours.closingTime) return null;
  return { openingTime: dayHours.openingTime, closingTime: dayHours.closingTime };
};

/**
 * Check if restaurant/store is currently open
 */
export const isCurrentlyOpen = (restaurantOrStore) => {
  if (restaurantOrStore?.closed === true) {
    return false; // Manually closed (e.g. ordering paused due to congestion)
  }
  if (!restaurantOrStore?.openingHours) {
    return true; // Default to open if no hours specified
  }

  const now = new Date();
  const dayName = getDayName(now);
  const dayHours = restaurantOrStore.openingHours[dayName];

  if (!dayHours || dayHours.isOpen === false) {
    return false; // Closed on this day
  }

  if (!dayHours.openingTime || !dayHours.closingTime) {
    return true; // Open all day if no specific times
  }

  const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  return currentTime >= dayHours.openingTime && currentTime <= dayHours.closingTime;
};

/**
 * Get the next available opening time for a restaurant/store
 * Returns a Date object for the next time the restaurant/store will be open
 */
export const getNextAvailableTime = (restaurantOrStore) => {
  if (!restaurantOrStore?.openingHours) {
    // If no hours specified, allow ordering for next hour
    const nextHour = new Date();
    nextHour.setHours(nextHour.getHours() + 1);
    nextHour.setMinutes(0);
    return nextHour;
  }

  const now = new Date();
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  
  // Check up to 7 days ahead
  for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
    const checkDate = new Date(now);
    checkDate.setDate(checkDate.getDate() + dayOffset);
    checkDate.setHours(0, 0, 0, 0); // Reset to start of day
    
    const dayName = getDayName(checkDate);
    const dayHours = restaurantOrStore.openingHours[dayName];

    if (!dayHours || dayHours.isOpen === false) {
      continue; // Skip closed days
    }

    if (!dayHours.openingTime) {
      // Open all day - return start of this day if today, otherwise tomorrow
      if (dayOffset === 0) {
        const today = new Date();
        today.setHours(now.getHours() + 1);
        today.setMinutes(0);
        return today;
      }
      checkDate.setHours(9, 0, 0, 0); // Default to 9 AM
      return checkDate;
    }

    const [openHour, openMinute] = dayHours.openingTime.split(':').map(Number);
    const openingTime = new Date(checkDate);
    openingTime.setHours(openHour, openMinute, 0, 0);

    // If it's today and we haven't passed the opening time yet, use it
    if (dayOffset === 0) {
      if (now < openingTime) {
        return openingTime;
      }
      // If we're past opening time today, check if we're still within closing time
      if (dayHours.closingTime) {
        const [closeHour, closeMinute] = dayHours.closingTime.split(':').map(Number);
        const closingTime = new Date(checkDate);
        closingTime.setHours(closeHour, closeMinute, 0, 0);
        
        if (now < closingTime) {
          // Still open today, return next hour
          const nextHour = new Date(now);
          nextHour.setHours(nextHour.getHours() + 1);
          nextHour.setMinutes(0);
          return nextHour;
        }
      }
    } else {
      // Future day - return opening time
      return openingTime;
    }
  }

  // If no opening time found in next 7 days, return next hour as fallback
  const nextHour = new Date(now);
  nextHour.setHours(nextHour.getHours() + 1);
  nextHour.setMinutes(0);
  return nextHour;
};

/**
 * Check if a specific date/time is during opening hours
 */
export const isTimeDuringOpeningHours = (restaurantOrStore, dateTime) => {
  if (!restaurantOrStore?.openingHours) {
    return true; // Default to valid if no hours specified
  }

  const dayName = getDayName(dateTime);
  const dayHours = restaurantOrStore.openingHours[dayName];

  if (!dayHours || dayHours.isOpen === false) {
    return false; // Closed on this day
  }

  if (!dayHours.openingTime || !dayHours.closingTime) {
    return true; // Open all day
  }

  const timeString = `${dateTime.getHours().toString().padStart(2, '0')}:${dateTime.getMinutes().toString().padStart(2, '0')}`;
  return timeString >= dayHours.openingTime && timeString <= dayHours.closingTime;
};

/**
 * Get available time slots for a specific date
 * Returns array of available time slots (every 15 minutes)
 */
export const getAvailableTimeSlots = (restaurantOrStore, date) => {
  if (!restaurantOrStore?.openingHours) {
    // Default: every 15 minutes from now to 24 hours ahead
    const slots = [];
    const start = new Date(date);
    start.setHours(9, 0, 0, 0);
    const end = new Date(date);
    end.setHours(22, 0, 0, 0);
    
    for (let time = new Date(start); time <= end; time.setMinutes(time.getMinutes() + 15)) {
      slots.push(new Date(time));
    }
    return slots;
  }

  const dayName = getDayName(date);
  const dayHours = restaurantOrStore.openingHours[dayName];

  if (!dayHours || dayHours.isOpen === false) {
    return []; // Closed on this day
  }

  if (!dayHours.openingTime || !dayHours.closingTime) {
    // Open all day - return slots from 9 AM to 10 PM
    const slots = [];
    const start = new Date(date);
    start.setHours(9, 0, 0, 0);
    const end = new Date(date);
    end.setHours(22, 0, 0, 0);
    
    for (let time = new Date(start); time <= end; time.setMinutes(time.getMinutes() + 15)) {
      slots.push(new Date(time));
    }
    return slots;
  }

  const slots = [];
  const [openHour, openMinute] = dayHours.openingTime.split(':').map(Number);
  const [closeHour, closeMinute] = dayHours.closingTime.split(':').map(Number);
  
  const start = new Date(date);
  start.setHours(openHour, openMinute, 0, 0);
  
  const end = new Date(date);
  end.setHours(closeHour, closeMinute, 0, 0);

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  for (let time = new Date(start); time <= end; time.setMinutes(time.getMinutes() + 15)) {
    // If it's today, only include future times
    if (isToday && time <= now) {
      continue;
    }
    slots.push(new Date(time));
  }

  return slots;
};
